// controllers/authController.js
import apiService from '../services/apiService';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Token + user localStorage mein rakho (sirf tab jab backend ne asli token diya ho)
// OTP pending response mein token hota hi nahi, isliye us waqt kuch save nahi hota
const saveSession = (response) => {
  if (response?.token) {
    localStorage.setItem('authToken', response.token);
  }
  if (response?.user) {
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem('isAuthenticated', 'true');
  }
};

// Backend ab OTP verify hone tak token nahi deta — challengeId deta hai (otpRequired: true).
// UI ko is result se pata chalta hai ki OTP (ya phone number) screen dikhani hai.
const otpPending = (response) => ({
  success: true,
  otpRequired: true,
  data: response,            // { challengeId, phoneMasked, phoneRequired?, retryAfterSeconds? }
  message: response.message,
});

// Error ko ek hi shape mein lao (errorCode / attemptsLeft bhi, jab apiService de)
const fail = (error, fallbackMessage) => {
  const body = error?.data || error?.response?.data || {};
  return {
    success: false,
    message: error?.message || fallbackMessage,
    status: error?.status,
    errorCode: error?.errorCode || body.errorCode,
    attemptsLeft: error?.attemptsLeft ?? body.attemptsLeft,
  };
};

export const authController = {
  // ───────────────────────────── LOGIN ─────────────────────────────
  // Login function — deviceId/deviceName/address/fcmToken bhi bhej sakta hai
  // Agar user ke paas phone number hai to backend OTP bhejta hai (result.otpRequired === true)
  login: async (email, password, extra = {}) => {
    try {
      const response = await apiService.post('users/login', { email, password, ...extra });

      if (response.otpRequired) return otpPending(response); // 🆕 token abhi nahi, OTP verify karo

      saveSession(response);
      return { success: true, data: response, message: 'Login successful' };
    } catch (error) {
      return fail(error, 'Login failed');
    }
  },

  // ───────────────────────────── OTP (login / register / google) ─────────────────────────────
  // 🆕 OTP verify — sahi OTP par token + user milta hai, aur yahin save ho jaata hai
  verifyOtp: async (challengeId, otp) => {
    try {
      const response = await apiService.post('users/auth/otp/verify', { challengeId, otp });
      saveSession(response);
      return { success: true, data: response, message: response.message || 'Verified' };
    } catch (error) {
      return fail(error, 'OTP verification failed');
    }
  },

  // 🆕 OTP dobara bhejo (60 sec cooldown backend par bhi lagta hai)
  resendOtp: async (challengeId) => {
    try {
      const response = await apiService.post('users/auth/otp/resend', { challengeId });
      return { success: true, data: response, message: response.message || 'OTP sent' };
    } catch (error) {
      return fail(error, 'Could not resend OTP');
    }
  },

  // 🆕 Google login par number na ho (phoneRequired) to number add/update karke OTP bhejo
  addPhoneForOtp: async (challengeId, phone) => {
    try {
      const response = await apiService.post('users/auth/otp/phone', { challengeId, phone });
      return { success: true, data: response, message: response.message || 'OTP sent' };
    } catch (error) {
      return fail(error, 'Could not send OTP');
    }
  },

  // ───────────────────────────── PROFILE ─────────────────────────────
  updateProfile: async (formData) => {
    try {
      // FormData ke liye putFormData use karo, put() nahi —
      // put() FormData ko JSON.stringify kar deta hai jo empty "{}" ban jata hai
      const response = await apiService.putFormData('users/profile', formData);

      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return {
        success: true,
        data: response,
        message: response.message || 'Profile updated successfully',
      };
    } catch (error) {
      return fail(error, 'Failed to update profile');
    }
  },

  getMySubscriptions: async () => {
    try {
      const response = await apiService.get('users/my-subscriptions');
      return { success: true, data: response };
    } catch (error) {
      return { success: false, data: { subscriptions: [] }, message: error.message || 'Failed to fetch subscriptions' };
    }
  },

  // ───────────────────────────── GOOGLE ─────────────────────────────
  // Google login — companyId/parentId/role/deviceId/address/fcmToken forward karta hai
  // Result: seedha token, ya otpRequired (number hai), ya otpRequired + data.phoneRequired (number nahi hai)
  loginWithGoogle: async ({ googleId, email, firstName, lastName, avatar, profile, phone, companyId, parentId, role, deviceId, deviceName, deviceType, address, fcmToken }) => {
    try {
      const response = await apiService.post('users/auth/google', {
        googleId, email, firstName, lastName, avatar, profile, phone,
        companyId, parentId, role,
        deviceId, deviceName, deviceType, address, fcmToken,
      });

      if (response.otpRequired) return otpPending(response); // 🆕

      saveSession(response);
      return { success: true, data: response, message: 'Google login successful' };
    } catch (error) {
      return fail(error, 'Google login failed');
    }
  },

  // 🆕 Forgot password — backend ke POST /api/users/forgot-password route
  // ko call karta hai. Login.jsx ka ForgotPasswordModal isko use karega.
  forgotPassword: async (email) => {
    try {
      const response = await apiService.post('users/forgot-password', { email });
      return {
        success: true,
        data: response,
        message: response.message || 'Reset link sent',
      };
    } catch (error) {
      return fail(error, 'Failed to send reset link');
    }
  },

  // ───────────────────────────── REGISTER ─────────────────────────────
  // Phone diya ho to backend OTP bhejta hai (result.otpRequired === true), warna seedha token
  register: async (userData) => {
    try {
      // ⚠️ Pehle 'auth/register' tha, par backend route 'users/register' hai (login ki tarah).
      // Agar aapka backend sach mein 'auth/register' par hai to wapas badal dena.
      const response = await apiService.post('users/register', userData);

      if (response.otpRequired) return otpPending(response); // 🆕

      saveSession(response);
      return { success: true, data: response, message: 'Registration successful' };
    } catch (error) {
      return fail(error, 'Registration failed');
    }
  },

  // ───────────────────────────── SESSION ─────────────────────────────
  // Logout function
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    return {
      success: true,
      message: 'Logged out successfully',
    };
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return localStorage.getItem('isAuthenticated') === 'true';
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Get auth token
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  getSessions: async () => {
    try {
      const response = await apiService.get('users/sessions');
      return { success: true, data: response };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to fetch sessions' };
    }
  },

  logoutSession: async (sessionId) => {
    try {
      const response = await apiService.delete(`users/sessions/${sessionId}`);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to logout device' };
    }
  },

  getLoginHistory: async () => {
    try {
      const response = await apiService.get('users/login-history');
      return { success: true, data: response };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to fetch login history' };
    }
  },
};

// Default export
export default authController;